import * as Api from '@ViewModels';
import { css, StyleDeclaration } from 'aphrodite';
import { styleSheet } from './styles';

const handwritingStyleFontMap: Partial<
	Record<Api.HandwritingStyle, { fontFamily: string; fontSize: number; lineHeight: number; charactersPerLine: number }>
> = {
	[Api.HandwritingStyle.Aladdin]: {
		fontFamily: Api.HandwritingStyle.Aladdin,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 68,
	},
	[Api.HandwritingStyle.Cinderella]: {
		fontFamily: Api.HandwritingStyle.Cinderella,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 75,
	},
	[Api.HandwritingStyle.Donald]: {
		fontFamily: Api.HandwritingStyle.Donald,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 75,
	},
	[Api.HandwritingStyle.Nemo]: {
		fontFamily: Api.HandwritingStyle.Nemo,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 75,
	},
	[Api.HandwritingStyle.Scar]: {
		fontFamily: Api.HandwritingStyle.Scar,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 75,
	},
	[Api.HandwritingStyle.Tarzan]: {
		fontFamily: Api.HandwritingStyle.Tarzan,
		fontSize: 30,
		lineHeight: 0.75,
		charactersPerLine: 63,
	},
	[Api.HandwritingStyle.Woody]: {
		fontFamily: Api.HandwritingStyle.Woody,
		fontSize: 22,
		lineHeight: 1,
		charactersPerLine: 75,
	},
};

// Template layout constants based on actual card dimensions
const CARD_LAYOUT = {
	landscapeHalf: {
		// Signature should be positioned from bottom for landscape-half cards
		signatureFromTop: '50%',
	},
	landscapeFull: {
		// Signature should be positioned from bottom for landscape-full cards
		signatureFromTop: '70%',
	},
};

function calculateSignaturePosition(
	postcardText: string,
	charactersPerLine: number
): {
	position: 'absolute';
	top: string;
	left: string;
} {
	const estimatedLines = postcardText
		.split('\n')
		.reduce((totalLines, line) => totalLines + Math.max(1, Math.ceil(line.length / charactersPerLine)), 0);

	// Estimate if this is a short message (landscape-half) or longer message (landscape-full)
	// using handwriting-style-specific wrapping so the signature preview tracks the chosen font.
	const isShortMessage = estimatedLines <= 8;

	const layout = isShortMessage ? CARD_LAYOUT.landscapeHalf : CARD_LAYOUT.landscapeFull;

	return {
		position: 'absolute',
		top: layout.signatureFromTop,
		left: '40%',
	};
}

export function HwcTextPreview({
	handwritingStyle,
	postcardText,
	signature,
	styleDeclaration,
}: {
	handwritingStyle: Api.HandwritingStyle;
	postcardText: string;
	signature: string;
	styleDeclaration?: StyleDeclaration;
}) {
	const fontSettings =
		handwritingStyleFontMap[handwritingStyle] || handwritingStyleFontMap[Api.HandwritingStyle.Aladdin];
	const signaturePosition = calculateSignaturePosition(postcardText, fontSettings.charactersPerLine);
	const hasSignature = signature.trim().length > 0;

	return (
		<div
			className={css(styleSheet.mainPreview, styleSheet.mainPreviewModeText, styleDeclaration)}
			style={{
				fontFamily: fontSettings.fontFamily,
				fontSize: fontSettings.fontSize,
				lineHeight: fontSettings.lineHeight,
				position: 'relative',
			}}
		>
			<p className={css(styleSheet.mainPreviewTextContent)}>{postcardText}</p>
			{hasSignature ? (
				<p className={css(styleSheet.mainPreviewSignature)} style={signaturePosition}>
					{signature}
				</p>
			) : null}
		</div>
	);
}
